import type { CarnivalRngState } from "../types";

const UINT32_SIZE = 4_294_967_296;
const MULBERRY_INCREMENT = 0x6d2b79f5;

function hashSeed(seed: string): number {
  let hash = 2_166_136_261;
  for (const char of seed) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

export function normalizeSeed(seed: string): string {
  const normalized = seed.trim();
  if (normalized.length === 0) throw new Error("Seed is required");
  return normalized;
}

export function createRngState(gameSeed: string): CarnivalRngState {
  return { algorithm: "mulberry32", state: hashSeed(normalizeSeed(gameSeed)) };
}

export interface RandomDraw {
  readonly rngState: CarnivalRngState;
  /** Uniform float in [0, 1). */
  readonly value: number;
}

export function nextRandom(rngState: CarnivalRngState): RandomDraw {
  const nextState = (rngState.state + MULBERRY_INCREMENT) >>> 0;
  let value = nextState;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return {
    rngState: { algorithm: "mulberry32", state: nextState },
    value: ((value ^ (value >>> 14)) >>> 0) / UINT32_SIZE,
  };
}

export interface IntDraw {
  readonly rngState: CarnivalRngState;
  readonly value: number;
}

/** Uniform integer in [1, faces], with faces=10 mapping the traditional 0-face to 10. */
export function nextDieFace(
  rngState: CarnivalRngState,
  faces: number,
): IntDraw {
  const draw = nextRandom(rngState);
  return { rngState: draw.rngState, value: Math.floor(draw.value * faces) + 1 };
}

/** Uniform float position draw in [0, max). */
export function nextBounded(rngState: CarnivalRngState, max: number): IntDraw {
  const draw = nextRandom(rngState);
  return { rngState: draw.rngState, value: draw.value * max };
}
